import { redirect } from "@tanstack/react-router";
import { explorerTarget, type ExplorerArea } from "./explorerArea";
import { serviceKey, useExplorerSessionState } from "./sessionState";
import { IssueReporter } from "utils/issueReporter";

/**
 * Redirects the bare Explorer path to the tab the reader had open. Both
 * layouts call this from `beforeLoad`, so the path is built in one place and
 * the trailing slash is normalised once.
 */
export const redirectIfAtExplorerRoot = (
  { pathname }: { pathname: string },
  params: { projectId: string; serviceId: string; branchId?: string },
): void => {
  const branch = params.branchId ? `/branches/${params.branchId}` : "";
  const root = `/projects/${params.projectId}/services/${params.serviceId}${branch}/explorer`;
  if (pathname.replace(/\/+$/, "") === root) {
    throw explorerIndexRedirect(params);
  }
};

/**
 * The redirect for the bare explorer path: to the selected tab of the service,
 * or to the home tab when no tab is selected.
 *
 * A branch route carries `branchId`, and its tabs and links stay in the branch
 * area. The tab state is keyed by the service the Explorer reads: the branch.
 */
export const explorerIndexRedirect = (params: {
  projectId: string;
  serviceId: string;
  branchId?: string;
}) => {
  const { projectId, serviceId, branchId } = params;
  const area: ExplorerArea = {
    isBranch: branchId !== undefined,
    projectId,
    serviceId,
    branchId: branchId ?? "",
  };
  const { selectedTabs, tabsByKey } = useExplorerSessionState.getState();
  const selectedTab =
    selectedTabs[serviceKey(projectId, branchId ?? serviceId)] ?? "home";
  const tab = tabsByKey[selectedTab];

  if (tab?.type) {
    switch (tab.type) {
      case "object":
        return redirect({
          ...explorerTarget(area, "object", {
            schema: tab.schema,
            objectType: tab.objectType,
            objectName: tab.objectName,
          }),
          replace: true,
        });
      case "query":
        return redirect({
          ...explorerTarget(area, "query", { queryId: tab.id }),
          replace: true,
        });
      default: {
        // @ts-expect-error: exhaustive switch on tab.type
        IssueReporter.error(`Unknown explorer tab type: ${tab.type}`);
        // fall through to the default route
      }
    }
  }

  return redirect({ ...explorerTarget(area, "home", {}), replace: true });
};
